import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  DollarSign, FileText, Calendar, Users, CheckCircle2,
  Loader2, Plus, Download, Clock, TrendingUp, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PayrollProcessing() {
  const { user } = useAuth();
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);
  const [activeTab, setActiveTab] = useState('payslips');
  const [payslips, setPayslips] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generateDialog, setGenerateDialog] = useState(false);
  const [generating, setGenerating] = useState(false);

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

  const [genForm, setGenForm] = useState({
    period_start: firstOfMonth,
    period_end: lastOfMonth
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const [pRes, sRes] = await Promise.all([
      axios.get(`${API_URL}/api/payroll/payslips`, { withCredentials: true }).catch(() => ({ data: [] })),
      isAdmin ? axios.get(`${API_URL}/api/payroll/summary`, { withCredentials: true }).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ]);
    setPayslips(pRes.data);
    setSummary(sRes.data);
    setLoading(false);
  };

  const generatePayroll = async () => {
    if (!genForm.period_start || !genForm.period_end) return toast.error('Select period');
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/payroll/generate`, genForm, { withCredentials: true });
      toast.success(`Generated ${data.total_payslips} payslips`);
      setGenerateDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    setGenerating(false);
  };

  const approvePayslip = async (id) => {
    try {
      await axios.put(`${API_URL}/api/payroll/payslips/${id}/approve`, {}, { withCredentials: true });
      toast.success('Payslip approved');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const tabs = [
    { id: 'payslips', label: 'Payslips', icon: FileText },
    ...(isAdmin ? [{ id: 'summary', label: 'Summary', icon: TrendingUp }] : []),
  ];

  const statusColors = {
    generated: 'bg-amber-500/10 text-amber-400',
    approved: 'bg-emerald-500/10 text-emerald-400',
    paid: 'bg-[#00A6A0]/10 text-[#00A6A0]'
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00A6A0]" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="payroll-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <DollarSign className="w-7 h-7 inline mr-3 text-[#00A6A0]" />Payroll
            </h1>
            <p className="text-muted-foreground mt-1">{isAdmin ? 'Process and manage payroll' : 'Your payslips'}</p>
          </div>
          {isAdmin && (
            <Button className="btn-primary" onClick={() => setGenerateDialog(true)} data-testid="generate-payroll-btn">
              <Plus className="w-4 h-4 mr-2" />Generate Payroll
            </Button>
          )}
        </div>

        {isAdmin && summary && (
          <div className="grid sm:grid-cols-4 gap-4">
            <Card className="glass-card border-border">
              <CardContent className="p-5">
                <p className="text-muted-foreground text-xs">Gross Pay</p>
                <p className="text-2xl font-bold text-[#00A6A0] mt-1">${summary.total_gross?.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-border">
              <CardContent className="p-5">
                <p className="text-muted-foreground text-xs">Deductions</p>
                <p className="text-2xl font-bold text-[#FF6F61] mt-1">${summary.total_deductions?.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-border">
              <CardContent className="p-5">
                <p className="text-muted-foreground text-xs">Net Pay</p>
                <p className="text-2xl font-bold text-foreground mt-1">${summary.total_net?.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-border">
              <CardContent className="p-5">
                <p className="text-muted-foreground text-xs">Caregivers</p>
                <p className="text-2xl font-bold text-[#A8E6CF] mt-1">{summary.caregiver_count}</p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="flex gap-2 border-b border-border pb-2">
          {tabs.map(t => (
            <Button key={t.id} variant="ghost" size="sm"
              className={activeTab === t.id ? 'text-[#00A6A0] border-b-2 border-[#00A6A0] rounded-none' : 'text-muted-foreground'}
              onClick={() => setActiveTab(t.id)} data-testid={`payroll-tab-${t.id}`}>
              <t.icon className="w-4 h-4 mr-1.5" />{t.label}
            </Button>
          ))}
        </div>

        {activeTab === 'payslips' && (
          <div className="space-y-3">
            {payslips.length === 0 ? (
              <Card className="glass-card border-border">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No payslips found</p>
                  {isAdmin && <p className="text-xs text-muted-foreground mt-1">Generate payroll to create payslips</p>}
                </CardContent>
              </Card>
            ) : (
              payslips.map(p => (
                <Card key={p.id} className="glass-card border-border" data-testid={`payslip-${p.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2.5 rounded-lg bg-[#00A6A0]/10">
                          <DollarSign className="w-5 h-5 text-[#00A6A0]" />
                        </div>
                        <div>
                          <p className="text-sm text-foreground font-medium">{p.caregiver_name}</p>
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                            <span>{p.period_start} - {p.period_end}</span>
                            <span className={`px-1.5 py-0.5 rounded-full ${statusColors[p.status] || statusColors.generated}`}>{p.status}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#00A6A0]">${p.net_pay?.toFixed(2)}</p>
                        <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-0.5">
                          <span>{p.total_hours}h</span>
                          <span>{p.shift_count} shifts</span>
                        </div>
                        {isAdmin && p.status === 'generated' && (
                          <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                            onClick={() => approvePayslip(p.id)} data-testid={`approve-payslip-${p.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-border">
                      <div className="text-center">
                        <p className="text-[10px] text-muted-foreground uppercase">Gross</p>
                        <p className="text-sm text-foreground">${p.gross_pay?.toFixed(2)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-muted-foreground uppercase">Deductions</p>
                        <p className="text-sm text-[#FF6F61]">-${p.deductions?.toFixed(2)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] text-muted-foreground uppercase">Net</p>
                        <p className="text-sm text-[#00A6A0] font-medium">${p.net_pay?.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {activeTab === 'summary' && isAdmin && summary && (
          <Card className="glass-card border-border">
            <CardHeader>
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#00A6A0]" />Payroll Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-xl border border-border">
                  <p className="text-xs text-muted-foreground uppercase mb-2">Financial</p>
                  <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-sm text-muted-foreground">Total Gross Pay</span><span className="text-sm text-foreground font-medium">${summary.total_gross?.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-muted-foreground">Total Deductions</span><span className="text-sm text-[#FF6F61]">-${summary.total_deductions?.toFixed(2)}</span></div>
                    <div className="flex justify-between border-t border-border pt-2"><span className="text-sm text-foreground font-medium">Total Net Pay</span><span className="text-sm text-[#00A6A0] font-bold">${summary.total_net?.toFixed(2)}</span></div>
                  </div>
                </div>
                <div className="p-4 bg-muted rounded-xl border border-border">
                  <p className="text-xs text-muted-foreground uppercase mb-2">Workforce</p>
                  <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-sm text-muted-foreground">Payslips Generated</span><span className="text-sm text-foreground">{summary.payslip_count}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-muted-foreground">Active Caregivers</span><span className="text-sm text-foreground">{summary.caregiver_count}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-muted-foreground">Total Hours</span><span className="text-sm text-foreground">{summary.total_hours?.toFixed(1)}h</span></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generate Dialog */}
        <Dialog open={generateDialog} onOpenChange={setGenerateDialog}>
          <DialogContent className="glass-card border-border max-w-sm">
            <DialogHeader><DialogTitle className="text-foreground">Generate Payroll</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Period Start</label>
                <Input type="date" value={genForm.period_start} onChange={e => setGenForm(f => ({ ...f, period_start: e.target.value }))}
                  className="bg-muted border-border text-foreground" data-testid="payroll-start-input" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Period End</label>
                <Input type="date" value={genForm.period_end} onChange={e => setGenForm(f => ({ ...f, period_end: e.target.value }))}
                  className="bg-muted border-border text-foreground" data-testid="payroll-end-input" />
              </div>
              <p className="text-xs text-muted-foreground">This will generate payslips for all caregivers with approved timesheets in the selected period.</p>
              <Button className="w-full btn-primary" onClick={generatePayroll} disabled={generating} data-testid="run-payroll-btn">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
                {generating ? 'Processing...' : 'Generate Payslips'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
